import { Link } from "react-router-dom";
import { Flame, MapPin, Phone, Mail, Clock, Facebook, Instagram, Youtube, Building2 } from "lucide-react";
import { bbqFooterLinks, socialLinks } from "@/lib/footerLinks";
import { CallRailPhone } from "@/components/shared/CallRailPhone";
import { useLocationContext, getLocationFooterLinks, getFooterDescription } from "@/hooks/useLocationContext";
import bbqLogoSm from "@/assets/bbq-logo-sm.webp";

export const BBQFooter = () => {
  const locationContext = useLocationContext();
  const { region, businessName, showroomName, address, email, hours, showroomLink, isShowroomLocation } = locationContext;
  const locationLinks = getLocationFooterLinks(region);
  
  // Determine if this is a Texas region (including sub-regions)
  const isTexasRegion = region === 'texas' || region === 'texas-houston' || region === 'texas-austin' || region === 'texas-san-antonio';
  const isCalifornia = region === 'california';
  
  // Get region-aware explore links
  const exploreLinks = [
    ...bbqFooterLinks.explore,
    ...(isTexasRegion ? bbqFooterLinks.exploreTexas : []),
    ...(isCalifornia ? bbqFooterLinks.exploreCalifornia : []),
  ];
  
  // Get region-aware services links (update showroom link for California)
  const servicesLinks = bbqFooterLinks.services.map(link => {
    if (link.href === '/showroom' && isCalifornia) {
      return { label: 'Showroom', href: '/showrooms/inland-empire/' };
    }
    return link;
  });

  // Render the national/multi-location footer for homepage
  if (region === 'national') {
    return <NationalFooter />;
  }

  return (
    <footer className="bg-[#1a1a1a] text-[#f5e6d3]">
      {/* Main Footer */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-7 gap-8">
          {/* Brand Column - Location Aware */}
          <div className="lg:col-span-2">
            <Link to="/">
              <img 
                src={bbqLogoSm} 
                alt="BBQ Island Liquidators" 
                className="h-16 w-auto mb-6" 
                width="68" 
                height="72"
                loading="lazy"
              />
            </Link>
            
            {/* Dynamic Business Name & Description */}
            <div className="mb-4">
              <p className="text-orange-400 font-semibold text-sm mb-1">{showroomName}</p>
              <p className="text-[#f5e6d3]/70 text-sm max-w-md">
                {getFooterDescription(region)}
              </p>
            </div>
            
            {/* Location-Specific Contact Info */}
            {isShowroomLocation && address.full && (
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-orange-500 mt-1 flex-shrink-0" />
                  <Link to={showroomLink} className="text-[#f5e6d3]/80 hover:text-orange-400 transition-colors">
                    {address.full}
                  </Link>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-orange-500 flex-shrink-0" />
                  <CallRailPhone 
                    source={`footer_${region}`}
                    className="text-orange-400 hover:text-orange-300 font-semibold callrail-phone"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-orange-500 flex-shrink-0" />
                  <a href={`mailto:${email}`} className="text-orange-400 hover:text-orange-300">
                    {email}
                  </a>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-orange-500 mt-1 flex-shrink-0" />
                  <div className="text-[#f5e6d3]/80">
                    <p>{hours.weekday}</p>
                    <p>{hours.weekend}</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Social Links */}
            <div className="flex gap-3 mt-6">
              <a href={socialLinks.bbq.facebook} target="_blank" rel="noopener noreferrer" aria-label="Follow us on Facebook" className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center hover:bg-orange-500/30 transition-colors">
                <Facebook className="w-5 h-5 text-orange-400" aria-hidden="true" />
              </a>
              <a href={socialLinks.bbq.instagram} target="_blank" rel="noopener noreferrer" aria-label="Follow us on Instagram" className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center hover:bg-orange-500/30 transition-colors">
                <Instagram className="w-5 h-5 text-orange-400" aria-hidden="true" />
              </a>
              <a href={socialLinks.bbq.youtube} target="_blank" rel="noopener noreferrer" aria-label="Subscribe to our YouTube channel" className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center hover:bg-orange-500/30 transition-colors">
                <Youtube className="w-5 h-5 text-orange-400" aria-hidden="true" />
              </a>
            </div>
          </div>

          {/* Shop Links */}
          <div>
            <h4 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Flame className="w-4 h-4 text-orange-500" />
              Shop
            </h4>
            <ul className="space-y-2">
              {bbqFooterLinks.shop.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="text-[#f5e6d3]/70 hover:text-orange-400 transition-colors text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Explore Links - Region Aware */}
          <div>
            <h4 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Flame className="w-4 h-4 text-orange-500" />
              Explore
            </h4>
            <ul className="space-y-2">
              {exploreLinks.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="text-[#f5e6d3]/70 hover:text-orange-400 transition-colors text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Brands Links */}
          <div>
            <h4 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Flame className="w-4 h-4 text-orange-500" />
              Popular Brands
            </h4>
            <ul className="space-y-2">
              {bbqFooterLinks.brands.map((link) => (
                <li key={link.href + link.label}>
                  <Link to={link.href} className="text-[#f5e6d3]/70 hover:text-orange-400 transition-colors text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services Links - Region Aware */}
          <div>
            <h4 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Flame className="w-4 h-4 text-orange-500" />
              Services
            </h4>
            <ul className="space-y-2">
              {servicesLinks.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="text-[#f5e6d3]/70 hover:text-orange-400 transition-colors text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
            <h4 className="font-bold text-lg mt-6 mb-4 flex items-center gap-2">
              <Flame className="w-4 h-4 text-orange-500" />
              B2B & Trade
            </h4>
            <ul className="space-y-2">
              {bbqFooterLinks.b2b.map((link) => (
                <li key={link.label}>
                  <Link to={link.href} className="text-[#f5e6d3]/70 hover:text-orange-400 transition-colors text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Location-Specific Links - Shows Texas OR California based on region */}
          <div>
            <h4 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Flame className="w-4 h-4 text-orange-500" />
              {isCalifornia ? 'California' : 'Texas'} Service Areas
            </h4>
            <ul className="space-y-2">
              {'locations' in locationLinks && locationLinks.locations.map((link) => (
                <li key={link.label}>
                  <Link to={link.href} className="text-[#f5e6d3]/70 hover:text-orange-400 transition-colors text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
            <h4 className="font-bold text-lg mt-6 mb-4 flex items-center gap-2">
              <Flame className="w-4 h-4 text-orange-500" />
              Resources
            </h4>
            <ul className="space-y-2">
              {bbqFooterLinks.resources.map((link) => (
                <li key={link.label}>
                  <Link to={link.href} className="text-[#f5e6d3]/70 hover:text-orange-400 transition-colors text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-[#3a3530]">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-[#f5e6d3]/50">
            <p>© {new Date().getFullYear()} {businessName}. All rights reserved.</p>
            <div className="flex items-center gap-6">
              {bbqFooterLinks.legal.map((link) => (
                <Link key={link.href} to={link.href} className="hover:text-orange-400">{link.label}</Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

/**
 * National/Homepage Footer - Shows both locations
 */
const NationalFooter = () => {
  const locationLinks = getLocationFooterLinks('national');

  return (
    <footer className="bg-[#1a1a1a] text-[#f5e6d3]">
      {/* Main Footer */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-7 gap-8">
          {/* Brand Column - Multi-Location */}
          <div className="lg:col-span-2">
            <Link to="/">
              <img src={bbqLogoSm} alt="BBQ Island Liquidators" className="h-16 w-auto mb-6" width="68" height="72" loading="lazy" />
            </Link>
            <p className="text-[#f5e6d3]/70 mb-6 max-w-md">
              America's premier destination for custom outdoor kitchens, premium grills, 
              smokers, and outdoor cooking equipment. Factory-direct pricing with showrooms 
              in Texas and California.
            </p>
            
            {/* Showroom Cards */}
            <div className="space-y-4">
              <Link 
                to="/showroom" 
                className="block p-3 rounded-lg bg-[#2a2520] hover:bg-[#3a3530] transition-colors border border-[#3a3530]"
              >
                <div className="flex items-start gap-3">
                  <Building2 className="w-5 h-5 text-orange-500 mt-0.5" />
                  <div>
                    <p className="text-orange-400 font-semibold text-sm">Dallas Showroom</p>
                    <p className="text-[#f5e6d3]/60 text-xs">2707 Realty Road, Carrollton, TX</p>
                  </div>
                </div>
              </Link>
              <Link 
                to="/showrooms/inland-empire/" 
                className="block p-3 rounded-lg bg-[#2a2520] hover:bg-[#3a3530] transition-colors border border-[#3a3530]"
              >
                <div className="flex items-start gap-3">
                  <Building2 className="w-5 h-5 text-orange-500 mt-0.5" />
                  <div>
                    <p className="text-orange-400 font-semibold text-sm">Inland Empire Showroom</p>
                    <p className="text-[#f5e6d3]/60 text-xs">14528 Meridian Pkwy, March ARB, CA</p>
                  </div>
                </div>
              </Link>
            </div>
            
            {/* Social Links */}
            <div className="flex gap-3 mt-6">
              <a href={socialLinks.bbq.facebook} target="_blank" rel="noopener noreferrer" aria-label="Follow us on Facebook" className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center hover:bg-orange-500/30 transition-colors">
                <Facebook className="w-5 h-5 text-orange-400" aria-hidden="true" />
              </a>
              <a href={socialLinks.bbq.instagram} target="_blank" rel="noopener noreferrer" aria-label="Follow us on Instagram" className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center hover:bg-orange-500/30 transition-colors">
                <Instagram className="w-5 h-5 text-orange-400" aria-hidden="true" />
              </a>
              <a href={socialLinks.bbq.youtube} target="_blank" rel="noopener noreferrer" aria-label="Subscribe to our YouTube channel" className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center hover:bg-orange-500/30 transition-colors">
                <Youtube className="w-5 h-5 text-orange-400" aria-hidden="true" />
              </a>
            </div>
          </div>

          {/* Shop Links */}
          <div>
            <h4 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Flame className="w-4 h-4 text-orange-500" />
              Shop
            </h4>
            <ul className="space-y-2">
              {bbqFooterLinks.shop.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="text-[#f5e6d3]/70 hover:text-orange-400 transition-colors text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Explore Links */}
          <div>
            <h4 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Flame className="w-4 h-4 text-orange-500" />
              Explore
            </h4>
            <ul className="space-y-2">
              {bbqFooterLinks.explore.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="text-[#f5e6d3]/70 hover:text-orange-400 transition-colors text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Brands Links */}
          <div>
            <h4 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Flame className="w-4 h-4 text-orange-500" />
              Popular Brands
            </h4>
            <ul className="space-y-2">
              {bbqFooterLinks.brands.map((link) => (
                <li key={link.href + link.label}>
                  <Link to={link.href} className="text-[#f5e6d3]/70 hover:text-orange-400 transition-colors text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services + B2B */}
          <div>
            <h4 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Flame className="w-4 h-4 text-orange-500" />
              Services
            </h4>
            <ul className="space-y-2">
              {bbqFooterLinks.services.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="text-[#f5e6d3]/70 hover:text-orange-400 transition-colors text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Locations - Both TX and CA */}
          <div>
            <h4 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Flame className="w-4 h-4 text-orange-500" />
              Texas
            </h4>
            <ul className="space-y-2">
              {'texas' in locationLinks && locationLinks.texas.map((link) => (
                <li key={link.label}>
                  <Link to={link.href} className="text-[#f5e6d3]/70 hover:text-orange-400 transition-colors text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
            <h4 className="font-bold text-lg mt-6 mb-4 flex items-center gap-2">
              <Flame className="w-4 h-4 text-orange-500" />
              California
            </h4>
            <ul className="space-y-2">
              {'california' in locationLinks && locationLinks.california.map((link) => (
                <li key={link.label}>
                  <Link to={link.href} className="text-[#f5e6d3]/70 hover:text-orange-400 transition-colors text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-[#3a3530]">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-[#f5e6d3]/50">
            <p>© {new Date().getFullYear()} BBQ Island Liquidators. All rights reserved.</p>
            <div className="flex items-center gap-6">
              {bbqFooterLinks.legal.map((link) => (
                <Link key={link.href} to={link.href} className="hover:text-orange-400">{link.label}</Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
